import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PackagesSectionProps {
  onConsultClick: () => void;
}

const PackagesSection = ({ onConsultClick }: PackagesSectionProps) => {
  const { t } = useTranslation('latvia');

  const commonCosts = [
    { label: t('packages.cost1.label'), amount: t('packages.cost1.amount'), icon: 'ri-funds-line' },
    { label: t('packages.cost2.label'), amount: t('packages.cost2.amount'), icon: 'ri-government-line' },
  ].filter((cost) => cost.label.trim() !== '');

  const excludedCosts = [
    t('packages.excluded1'),
    t('packages.excluded2'),
    t('packages.excluded3'),
  ];

  return (
    <section className="py-6 md:py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6 md:mb-16">
          <span className="inline-block px-3 py-1 md:px-4 md:py-1.5 bg-gold-50 text-gold-700 text-[9px] md:text-xs font-semibold tracking-widest uppercase rounded-full mb-2 md:mb-4">
            {t('packages.badge')}
          </span>
          <h2 className="text-xl md:text-5xl font-bold text-gray-900 mb-2 md:mb-4 font-serif">
            {t('packages.title')}
          </h2>
          <p className="text-[11px] md:text-base text-gray-500 max-w-2xl mx-auto leading-relaxed">
            {t('packages.description')}
          </p>
        </div>

        {/* Common Costs Section */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 gap-3 md:gap-6 mb-6 md:mb-10">
          {/* Included Costs */}
          <div className="bg-white rounded-xl md:rounded-2xl border border-gray-100 shadow-sm p-3.5 md:p-8 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-6">
              <div className="w-6 h-6 md:w-10 md:h-10 flex items-center justify-center rounded-lg md:rounded-xl bg-emerald-50">
                <i className="ri-checkbox-circle-line text-emerald-500 text-xs md:text-xl"></i>
              </div>
              <h3 className="text-xs md:text-lg font-bold text-gray-900">{t('packages.common.title')}</h3>
            </div>
            <div className="space-y-2 md:space-y-5">
              {commonCosts.map((cost, idx) => (
                <div key={idx} className="flex items-center justify-between py-2 md:py-4 px-2.5 md:px-5 bg-gray-50 rounded-lg md:rounded-xl">
                  <div className="flex items-center gap-1.5 md:gap-3">
                    <div className="w-5 h-5 md:w-9 md:h-9 flex items-center justify-center rounded-md md:rounded-lg bg-white shadow-sm">
                      <i className={`${cost.icon} text-gold-600 text-[10px] md:text-lg`}></i>
                    </div>
                    <span className="text-[10px] md:text-sm font-medium text-gray-700">{cost.label}</span>
                  </div>
                  <span className="text-xs md:text-xl font-bold text-gray-900">{cost.amount}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Excluded Costs */}
          <div className="bg-white rounded-xl md:rounded-2xl border border-gray-100 shadow-sm p-3.5 md:p-8 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-6">
              <div className="w-6 h-6 md:w-10 md:h-10 flex items-center justify-center rounded-lg md:rounded-xl bg-red-50">
                <i className="ri-information-line text-red-400 text-xs md:text-xl"></i>
              </div>
              <h3 className="text-xs md:text-lg font-bold text-gray-900">{t('packages.excluded.title')}</h3>
            </div>
            <div className="space-y-2 md:space-y-4">
              {excludedCosts.map((item, idx) => (
                <div key={idx} className="flex items-start gap-1.5 md:gap-3 py-2 md:py-3 px-2.5 md:px-5 bg-red-50/50 rounded-lg md:rounded-xl">
                  <div className="w-3.5 h-3.5 md:w-5 md:h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-close-line text-red-400 text-[10px] md:text-base"></i>
                  </div>
                  <span className="text-[10px] md:text-sm text-gray-600 leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Note */}
        <div className="mt-6 md:mt-10 text-center">
          <div className="inline-flex items-center gap-1.5 md:gap-2 px-3 md:px-5 py-2 md:py-3 bg-gray-50 rounded-full">
            <i className="ri-shield-check-line text-gold-600 text-xs md:text-base"></i>
            <p className="text-gray-500 text-[9px] md:text-xs">
              {t('packages.note')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PackagesSection;